import React, { useContext, useState, useEffect } from "react";
import { Redirect, useRouteMatch } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import styles from "../styles.module.scss";
import resourcesStyles from "./styles.module.scss";
import Tabs from "app/components/Tabs";
import { useAdminSettingsTabs } from "app/routes/Facility/adminSettingsTabs";
import { UserContext } from "app/contexts/UserContext";
import { cls } from "app/utils";
import useCurrentFacility from "app/utils/hooks/scheduling/useCurrentFacility";
import { isFacilityStaff } from "app/utils/roleAccess";
import {
  getFacilityResources,
  createFacilityResource,
  updateFacilityResource,
  deleteFacilityResource,
  sortFacilityResources,
  ScheduleResourceResponse,
  ScheduleResourcePayload,
  ResourceCategoryResponse,
  ResourceOrderPayload,
} from "app/utils/request/scheduling/facilityResources";
import {
  getResourceCategories,
  createResourceCategory,
  updateResourceCategory,
  deleteResourceCategory,
} from "app/utils/request/scheduling/resourceCategories";
import CancelPurchaseConfirmationModal from "app/components/CancelPurchaseConfirmationModal";
import ResourcesView from "./ResourcesView";
import { SectionValues } from "./SectionModal";

interface ApiError {
  errors?: Record<string, string[]>;
}

// The confirm modal serves a resource and a section.
type PendingDelete = { type: "resource" | "section"; id: number };

// Admins and Coach Admins edit on the first path. Coaches and schedulers read
// on the second path, without the Admin Settings tabs.
const EDIT_PATH = "/manage_facility/resources";
const VIEW_PATH = "/facility_resources";

const Resources = () => {
  const { t } = useTranslation();
  const user = useContext(UserContext);
  const currentFacility = useCurrentFacility();
  const tabs = useAdminSettingsTabs("resources");
  const isEditPath = useRouteMatch(EDIT_PATH) !== null;

  const [resources, setResources] = useState<ScheduleResourceResponse[]>([]);
  const [categories, setCategories] = useState<ResourceCategoryResponse[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [refreshTrigger, setRefreshTrigger] = useState(false);

  const [pendingDelete, setPendingDelete] = useState<PendingDelete | null>(
    null
  );
  const [isDeleting, setIsDeleting] = useState(false);

  const facilityId = currentFacility.data?.id;

  const hasResourceManagement =
    !!currentFacility.data?.featureFlags?.resourceManagement;

  const canView = isFacilityStaff(user) && hasResourceManagement;

  const canEdit = !!user.behavesLikeAdmin && hasResourceManagement;

  // Before the facility loads, the path and the role select the frame.
  const showEditor = currentFacility.pending
    ? isEditPath && !!user.behavesLikeAdmin
    : canEdit;

  useEffect(() => {
    let ignore = false;
    setResources([]);
    setCategories([]);
    if (!facilityId || !canView) {
      return undefined;
    }
    setIsLoading(true);
    Promise.all([
      getResourceCategories(facilityId),
      getFacilityResources(facilityId),
    ])
      .then(([categoriesData, resourcesData]) => {
        if (ignore) {
          return;
        }
        // This version shows bookable resources only. The page leaves out the
        // location sections and their resources.
        setCategories(
          categoriesData.filter((category) => category.kind === "bookable")
        );
        setResources(
          resourcesData.filter((resource) => resource.kind === "bookable")
        );
      })
      .catch((error) => {
        if (ignore) {
          return;
        }
        console.error("Error fetching resources:", error);
        toast.error(t("Failed to load resources."));
      })
      .finally(() => {
        if (!ignore) {
          setIsLoading(false);
        }
      });

    return () => {
      ignore = true;
    };
  }, [facilityId, canView, refreshTrigger, t]);

  // Returns true when the write succeeds, so the modal stays open on a failure
  // and keeps what the user typed.
  const handleAddResource = async (payload: ScheduleResourcePayload) => {
    if (!facilityId) {
      return false;
    }
    try {
      await createFacilityResource(facilityId, payload);
      setRefreshTrigger((previous) => !previous);
      toast.success(t("Resource created successfully."));

      return true;
    } catch (error) {
      console.error("Error creating resource:", error);
      toast.error(t("Failed to create resource."));

      return false;
    }
  };

  const handleEditResource = async (
    resourceId: number,
    payload: ScheduleResourcePayload
  ) => {
    if (!facilityId) {
      return false;
    }
    try {
      await updateFacilityResource(facilityId, resourceId, payload);
      setRefreshTrigger((previous) => !previous);
      toast.success(t("Resource updated successfully."));

      return true;
    } catch (error) {
      console.error("Error updating resource:", error);
      toast.error(t("Failed to update resource."));

      return false;
    }
  };

  // The Deactivate and Activate items of a card menu. Writes the state to the
  // page first, then to the API. No refresh follows, so the card keeps its
  // place in the list.
  const handleToggleActive = async (resource: ScheduleResourceResponse) => {
    const wanted = !resource.active;
    const apply = (value: boolean) =>
      setResources((previous) =>
        previous.map((item) =>
          item.id === resource.id ? { ...item, active: value } : item
        )
      );

    if (!facilityId) {
      return;
    }
    apply(wanted);
    try {
      await updateFacilityResource(facilityId, resource.id, { active: wanted });
    } catch (error) {
      console.error("Error updating resource:", error);
      apply(resource.active);
      toast.error(t("Failed to update resource."));
    }
  };

  // Creates a section when `id` is absent, else renames it. A failure with
  // field errors goes back to the modal, which shows them under the fields.
  const handleSaveSection = async (values: SectionValues) => {
    if (!facilityId) {
      return;
    }
    try {
      if (values.id) {
        await updateResourceCategory(facilityId, values.id, {
          title: values.title,
        });
        toast.success(t("Section updated successfully."));
      } else {
        await createResourceCategory(facilityId, {
          title: values.title,
          kind: "bookable",
        });
        toast.success(t("Section created successfully."));
      }
      setRefreshTrigger((previous) => !previous);
    } catch (error) {
      console.error("Error saving section:", error);
      if (!(error as ApiError)?.errors) {
        toast.error(t("Failed to save section."));
      }
      throw error;
    }
  };

  // Saves the order of the reorder mode, then reloads the page data. Returns
  // true when the write succeeds, so the reorder mode stays open on a failure
  // and keeps the drags.
  const handleSaveOrder = async (order: ResourceOrderPayload) => {
    if (!facilityId) {
      return false;
    }
    try {
      await sortFacilityResources(facilityId, order);
      setRefreshTrigger((previous) => !previous);
      toast.success(t("Order saved successfully"));

      return true;
    } catch (error) {
      console.error("Error saving order:", error);
      toast.error(t("Failed to save order"));

      return false;
    }
  };

  const handleDeleteResource = async (resourceId: number) => {
    setPendingDelete({ type: "resource", id: resourceId });
  };

  const handleDeleteSection = (categoryId: number) => {
    setPendingDelete({ type: "section", id: categoryId });
  };

  const handleConfirmDelete = async () => {
    if (!facilityId || pendingDelete === null) {
      return;
    }

    const isSection = pendingDelete.type === "section";
    setIsDeleting(true);
    try {
      if (isSection) {
        await deleteResourceCategory(facilityId, pendingDelete.id);
      } else {
        await deleteFacilityResource(facilityId, pendingDelete.id);
      }
      setRefreshTrigger((previous) => !previous);
      toast.success(
        isSection
          ? t("Section deleted successfully.")
          : t("Resource deleted successfully.")
      );
    } catch (err) {
      const error = err as ApiError;
      console.error("Error deleting:", error);
      // The API refuses a resource a booking option uses, and one with a future
      // booking. It refuses a section that holds a resource, and one a booking
      // option uses. The reason arrives in :base.
      const reason = error?.errors?.base?.[0];
      toast.error(
        reason ||
          (isSection
            ? t("Failed to delete section.")
            : t("Failed to delete resource."))
      );
    } finally {
      setIsDeleting(false);
      setPendingDelete(null);
    }
  };

  const handleCancelDelete = () => {
    setPendingDelete(null);
  };

  const isSectionDelete = pendingDelete?.type === "section";

  // Handles a typed URL. The tab and the sidebar item are hidden without the
  // feature. A user on the path of the other mode goes to the path of their
  // mode.
  if (!currentFacility.pending) {
    if (!canView) {
      return <Redirect to={user.behavesLikeAdmin ? "/manage_facility" : "/"} />;
    }
    if (canEdit !== isEditPath) {
      return <Redirect to={canEdit ? EDIT_PATH : VIEW_PATH} />;
    }
  }

  const view = (
    <ResourcesView
      isLoading={isLoading}
      readOnly={!showEditor}
      resources={resources}
      categories={categories}
      onAdd={handleAddResource}
      onEdit={handleEditResource}
      onToggleActive={handleToggleActive}
      onDelete={handleDeleteResource}
      onSaveSection={handleSaveSection}
      onDeleteSection={handleDeleteSection}
      onSaveOrder={handleSaveOrder}
    />
  );

  if (!showEditor) {
    return (
      <div className={cls("admin-settings-container", resourcesStyles.read_only_page)}>
        {view}
      </div>
    );
  }

  return (
    <div className="admin-settings-container">
      <div className={styles.title}>
        <h1>{t("Admin Settings")}</h1>
      </div>
      <div>
        <div className={styles.tabs_container}>
          <Tabs tabs={tabs} scrollable />
        </div>
        {view}
      </div>
      <CancelPurchaseConfirmationModal
        isOpen={pendingDelete !== null}
        onConfirm={handleConfirmDelete}
        onCancel={handleCancelDelete}
        isLoading={isDeleting}
        title={isSectionDelete ? t("Delete Section") : t("Delete Resource")}
        message={
          isSectionDelete
            ? t("Are you sure you want to delete this section?")
            : t("Are you sure you want to delete this resource?")
        }
      />
    </div>
  );
};

export default Resources;
